/**
 * @fileoverview Download Job Manager for handling asynchronous DICOM downloads
 * Manages job state persistence, polling, and cleanup
 */

import { isPendingStatus } from "./download-handler";
import { DOWNLOAD_CONFIG } from "../config/download-config";

/**
 * Download Job Manager
 * Handles persistence and retrieval of download jobs across page refreshes
 */
class DownloadJobManager {
  /**
   * Get all stored jobs
   */
  getAllJobs() {
    try {
      const stored = localStorage.getItem(DOWNLOAD_CONFIG.STORAGE_KEY);
      if (!stored) return {};

      const jobs = JSON.parse(stored);

      // Clean up old jobs
      return this.cleanupOldJobs(jobs);
    } catch (error) {
      console.error("Error reading download jobs from storage:", error);
      return {};
    }
  }

  /**
   * Save all jobs to storage
   */
  saveAllJobs(jobs) {
    try {
      localStorage.setItem(DOWNLOAD_CONFIG.STORAGE_KEY, JSON.stringify(jobs));
    } catch (error) {
      console.error("Error saving download jobs to storage:", error);
    }
  }

  /**
   * Remove jobs older than MAX_JOB_AGE
   */
  cleanupOldJobs(jobs) {
    const now = Date.now();
    const maxAge = DOWNLOAD_CONFIG.MAX_JOB_AGE;

    return Object.fromEntries(
      Object.entries(jobs).filter(([_, job]) => {
        return now - job.stored_at < maxAge;
      })
    );
  }

  /**
   * Store a new download job
   */
  storeJob(jobData, resourceType, filename) {
    const jobs = this.getAllJobs();

    const storedJob = {
      ...jobData,
      stored_at: Date.now(),
      polling_count: 0,
      resource_type: resourceType,
      filename,
    };

    jobs[jobData.job_id] = storedJob;
    this.saveAllJobs(jobs);

    console.log(`Stored download job: ${jobData.job_id}`, storedJob);
  }

  /**
   * Get a specific job by ID
   */
  getJob(jobId) {
    const jobs = this.getAllJobs();
    return jobs[jobId] || null;
  }

  /**
   * Update job status (after polling)
   */
  updateJob(jobId, updates) {
    const jobs = this.getAllJobs();
    const job = jobs[jobId];

    if (!job) {
      console.warn(`Job ${jobId} not found in storage`);
      return null;
    }

    // Update job with new data
    jobs[jobId] = {
      ...job,
      ...updates,
      polling_count: job.polling_count + 1,
    };

    this.saveAllJobs(jobs);
    return jobs[jobId];
  }

  /**
   * Remove a job from storage (after successful download or failure)
   */
  removeJob(jobId) {
    const jobs = this.getAllJobs();
    delete jobs[jobId];
    this.saveAllJobs(jobs);

    console.log(`Removed download job: ${jobId}`);
  }

  /**
   * Get all pending jobs (for recovery on page load)
   * Includes both "pending" and "processing" statuses
   */
  getPendingJobs() {
    const jobs = this.getAllJobs();

    return Object.values(jobs).filter((job) => isPendingStatus(job.status));
  }

  /**
   * Check if a job should continue polling
   */
  shouldContinuePolling(job) {
    return job.polling_count < DOWNLOAD_CONFIG.MAX_POLLING_ATTEMPTS;
  }

  /**
   * Clear all stored jobs (for cleanup/reset)
   */
  clearAllJobs() {
    localStorage.removeItem(DOWNLOAD_CONFIG.STORAGE_KEY);
    console.log("Cleared all download jobs from storage");
  }

  /**
   * Get job statistics
   */
  getJobStats() {
    const jobs = this.getAllJobs();
    const jobList = Object.values(jobs);

    return {
      total: jobList.length,
      pending: jobList.filter((j) => isPendingStatus(j.status)).length,
      completed: jobList.filter((j) => j.status === "completed").length,
      failed: jobList.filter((j) => j.status === "failed").length,
    };
  }
}

// Export singleton instance
export const downloadJobManager = new DownloadJobManager();

